import type { ReactNode } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/** Tom do quadro — define fundo, borda e cor do ícone. */
export const tonsPainel = ['ajuda', 'info', 'neutro'] as const;
export type TomPainel = (typeof tonsPainel)[number];

interface CoresPainel {
  fundo: string;
  borda: string;
  icone: string;
  fundoIcone: string;
}

/**
 * Cores derivadas da paleta do tema, nunca fixas em hex: o backoffice tem modo
 * claro e escuro, e um amarelo cravado no código viraria borrão ilegível em um
 * dos dois. O fundo é translúcido (`alpha`) sobre o papel do tema, então o
 * mesmo valor serve aos dois modos; só o tom do ícone troca, porque o
 * `warning.dark` desaparece no fundo escuro e o `warning.light` estoura no claro.
 */
function coresDoTom(theme: Theme, tom: TomPainel): CoresPainel {
  if (tom === 'neutro') {
    return {
      fundo: theme.palette.action.hover,
      borda: theme.palette.divider,
      icone: theme.palette.text.secondary,
      fundoIcone: theme.palette.action.selected,
    };
  }
  const cor = tom === 'ajuda' ? theme.palette.warning : theme.palette.info;
  const escuro = theme.palette.mode === 'dark';
  return {
    fundo: alpha(cor.main, escuro ? 0.12 : 0.09),
    borda: alpha(cor.main, escuro ? 0.32 : 0.26),
    icone: escuro ? cor.light : cor.dark,
    fundoIcone: alpha(cor.main, escuro ? 0.22 : 0.16),
  };
}

interface PainelInfoProps {
  titulo: string;
  /** Pontos em lista. Opcional: um quadro pode ser só `children`. */
  itens?: ReactNode[];
  /**
   * Conteúdo livre abaixo da lista — campos copiáveis, botões, passo a passo que
   * abre. Herda `body2`/`text.secondary`, então o texto sai igual ao dos itens.
   */
  children?: ReactNode;
  /** Ícone do quadro. Padrão: interrogação — o quadro é material de ajuda. */
  icone?: ReactNode;
  /** Padrão: `ajuda` (amarelo discreto). Use `neutro` para apoio sem destaque. */
  tom?: TomPainel;
  /** Versão compacta, para o quadro que vive dentro de um diálogo. */
  denso?: boolean;
}

/**
 * Quadro de ajuda do backoffice: um título e uma lista de pontos que o gestor
 * precisa saber para operar a tela.
 *
 * O amarelo é DE PROPÓSITO mais fraco que o do `Alert severity="warning"`: um
 * fundo translúcido diz "leia isto uma vez" sem competir com os avisos que
 * pedem ação — alerta em força total em toda abertura de tela vira ruído e some
 * da vista em uma semana.
 *
 * `itens` são os pontos que valem para a operação — não repita aqui o que os
 * campos da tela já explicam.
 */
export function PainelInfo({
  titulo,
  itens,
  children,
  icone = <HelpOutlineIcon />,
  tom = 'ajuda',
  denso = false,
}: PainelInfoProps): JSX.Element {
  const cores = coresDoTom(useTheme(), tom);
  const lado = denso ? 36 : 44;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: denso ? { xs: 2, md: 2.5 } : { xs: 2.5, md: 4 },
        bgcolor: cores.fundo,
        borderColor: cores.borda,
      }}
    >
      <Stack direction="row" spacing={denso ? 1.5 : 2} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: lado,
            height: lado,
            borderRadius: 2,
            bgcolor: cores.fundoIcone,
            color: cores.icone,
            flexShrink: 0,
          }}
        >
          {icone}
        </Box>

        {/* `flex: 1` para o conteúdo ocupar a linha: campo copiável é `fullWidth`
            e, sem isso, encolheria até o tamanho do texto. */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={denso ? 'subtitle1' : 'h6'} sx={{ lineHeight: 1.3 }}>
            {titulo}
          </Typography>

          {itens && itens.length > 0 && (
            <Box
              component="ul"
              sx={{
                pl: 2.5,
                mt: 1.5,
                mb: 0,
                color: 'text.secondary',
                typography: 'body2',
                '& li + li': { mt: 1 },
              }}
            >
              {itens.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </Box>
          )}

          {children && (
            <Box sx={{ mt: 1.5, color: 'text.secondary', typography: 'body2' }}>{children}</Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * O quadro das abas de modo. Abre cada aba dizendo o essencial, começando por
 * ONDE OS PEDIDOS CAEM no Sischef — isso não estava escrito em lugar nenhum, e
 * a única forma de descobrir era fazer um pedido de teste e sair procurando.
 */
export function InfoModo({ itens }: { itens: ReactNode[] }): JSX.Element {
  return <PainelInfo titulo="Como funciona este modo" itens={itens} />;
}
